import React, { useState, useEffect } from "react";

const parseIsoDateToUtc = (isoDate) => {
    if (!isoDate) return null;
    const [year, month, day] = isoDate.split('-').map(Number);
    if (!year || !month || !day) return null;
    return Date.UTC(year, month - 1, day);
};

const MONTH_OPTIONS = [
    { value: "", label: "Month" },
    { value: "01", label: "Jan" },
    { value: "02", label: "Feb" },
    { value: "03", label: "Mar" },
    { value: "04", label: "Apr" },
    { value: "05", label: "May" },
    { value: "06", label: "Jun" },
    { value: "07", label: "Jul" },
    { value: "08", label: "Aug" },
    { value: "09", label: "Sep" },
    { value: "10", label: "Oct" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
];

const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(Number(year), Number(month), 0).getDate();
};

const FormattedDateInput = ({ value, onChange, className = "", disabled = false, name = "date", min }) => {
    const [day, setDay] = useState("");
    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");
    const [error, setError] = useState(false);
    const minUtc = parseIsoDateToUtc(min);

    useEffect(() => {
        if (!value) {
            setDay("");
            setMonth("");
            setYear("");
            setError(false);
            return;
        }

        const [y, m, d] = value.split("-");
        setDay(d || "");
        setMonth(m || "");
        setYear(y || "");
    }, [value]);

    const tryCommitDate = (nextDay, nextMonth, nextYear) => {
        if (!nextDay && !nextMonth && !nextYear) {
            onChange({ target: { value: "", name } });
            setError(false);
            return;
        }

        if (!nextDay || !nextMonth || !nextYear || nextYear.length !== 4) {
            setError(false);
            return;
        }

        const dayNum = Number(nextDay);
        const monthNum = Number(nextMonth);
        const yearNum = Number(nextYear);
        const dateObj = new Date(yearNum, monthNum - 1, dayNum);
        const isValidDate =
            dateObj.getFullYear() === yearNum &&
            dateObj.getMonth() === monthNum - 1 &&
            dateObj.getDate() === dayNum;

        if (!isValidDate) {
            setError(true);
            return;
        }

        const isoDate = `${nextYear}-${nextMonth}-${nextDay}`;
        if (minUtc && parseIsoDateToUtc(isoDate) < minUtc) {
            setError(true);
            return;
        }

        setError(false);
        onChange({ target: { value: isoDate, name } });
    };
    const [minYear] = (min || "0001-01-01").split("-");
    const yearMin = Number(minYear) || 1;
    const yearMax = new Date().getFullYear();
    const yearOptions = [];
    for (let y = yearMax; y >= yearMin; y -= 1) {
        yearOptions.push(String(y));
    }

    const maxDays = getDaysInMonth(year || minYear, month || "01");
    const dayOptions = Array.from({ length: maxDays }, (_, idx) => String(idx + 1).padStart(2, "0"));
    const controlStyle = {
        width: "100%",
        borderRadius: "0.45rem",
        border: "1px solid rgba(209, 213, 219, 1)",
        padding: "0.4rem 0.55rem",
        fontSize: "0.875rem",
        backgroundColor: "#fff",
        color: "#111827",
    };

    return (
        <div style={{ position: "relative", width: "100%" }} className={className}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: "0.5rem", alignItems: "center" }}>
                <select
                    value={day}
                    onChange={(e) => {
                        const nextDay = e.target.value;
                        setDay(nextDay);
                        tryCommitDate(nextDay, month, year);
                    }}
                    disabled={disabled}
                    name={`${name}-day`}
                    style={controlStyle}
                >
                    <option value="">Day</option>
                    {dayOptions.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
                <select
                    value={month}
                    onChange={(e) => {
                        const nextMonth = e.target.value;
                        setMonth(nextMonth);
                        // reset day if it exceeds month max
                        const maxDayForMonth = getDaysInMonth(year || minYear, nextMonth || "01");
                        const adjustedDay = day && Number(day) > maxDayForMonth ? "" : day;
                        if (adjustedDay !== day) {
                            setDay(adjustedDay);
                        }
                        tryCommitDate(adjustedDay, nextMonth, year);
                    }}
                    disabled={disabled}
                    name={`${name}-month`}
                    style={controlStyle}
                >
                    {MONTH_OPTIONS.map((option) => (
                        <option key={option.value || "month"} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                <select
                    value={year}
                    onChange={(e) => {
                        const nextYear = e.target.value;
                        setYear(nextYear);
                        const maxDayForMonth = getDaysInMonth(nextYear || minYear, month || "01");
                        const adjustedDay = day && Number(day) > maxDayForMonth ? "" : day;
                        if (adjustedDay !== day) {
                            setDay(adjustedDay);
                        }
                        tryCommitDate(adjustedDay, month, nextYear);
                    }}
                    disabled={disabled}
                    name={`${name}-year`}
                    style={controlStyle}
                >
                    <option value="">Year</option>
                    {yearOptions.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            </div>

            {error && (
                <p style={{ color: "#d64545", fontSize: "0.8rem", margin: "0.4rem 0 0" }}>
                    Please select a valid date on or after {min}.
                </p>
            )}
        </div>
    );
};

export default FormattedDateInput;
